import { useEffect, useRef, useState } from "react";
import UserApi from "../api/Admin/User";
import { ValidationStatus, ValidationTextField } from "./ValidationTextField";
import { useDebounce } from "../hooks/useDebounce";
import { GlobalButton } from "../pages/styles";
import { useUser } from "../context/UserContext";
import { Stack } from "@mui/system";

interface ValidateEmailAddressTextFieldProps {
  placeholder: string;
  value: string;
  onChange: (next: string) => void;
  onStatusChange?: (status: ValidationStatus) => void;
  debounceMs?: number;
  minChars?: number;
  label?: string;
  disabled?: boolean;
  showPlusMeButton: boolean;
}

export const ValidateEmailAddressTextField = ({
  placeholder = "",
  value,
  onChange,
  onStatusChange,
  debounceMs = 1000,
  minChars = 2,
  label = "Email Address",
  disabled = false,
  showPlusMeButton = false,
  ...props
}: ValidateEmailAddressTextFieldProps) => {
  const [status, setStatus] = useState<ValidationStatus>("idle");
  const [helperText, setHelperText] = useState("");
  const abortRef = useRef<AbortController | null>(null);

  const debouncedValue = useDebounce(value, debounceMs);
  const { username: userEmail } = useUser();

  useEffect(() => {
    onStatusChange?.(status);
  }, [status, onStatusChange]);

  useEffect(() => {
    if (!debouncedValue || debouncedValue.trim().length < minChars) {
      setStatus("idle");
      setHelperText(debouncedValue ? `Type at least ${minChars} characters` : "");
      abortRef.current?.abort();
      return;
    }

    const validateChemId = async () => {
      setStatus("loading");
      setHelperText("Checking…");

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      UserApi.validateEmailAddress(debouncedValue.trim())
        .then((response) => {
          if (response) {
            setStatus("valid");
            setHelperText("Valid Email");
          } else {
            setStatus("invalid");
            setHelperText("Invalid Email");
          }
        })
        .catch((error) => {
          if (error.name === "AbortError") return;
          setStatus("error");
          setHelperText("Lookup failed");
        });
    };

    validateChemId();
  }, [debouncedValue, minChars]);

  const handlePlusMeButtonClick = () => {
    if (userEmail) {
      onChange(userEmail);
    }
  };

  return (
    <Stack direction="row" spacing={showPlusMeButton ? 1 : 0} alignItems={"flex-start"}>
      <ValidationTextField
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        variant="outlined"
        helperText={helperText}
        disabled={disabled}
        size="small"
        margin="normal"
        InputLabelProps={{ shrink: true }}
        status={status}
        sx={{ flexGrow: 1 }} // check this
        {...props}
      />
      {showPlusMeButton && (
        <GlobalButton
          name="btnMe"
          disabled={disabled}
          variant="contained"
          onClick={handlePlusMeButtonClick}
          sx={{ mt: 2 }}>
          +Me
        </GlobalButton>
      )}
    </Stack>
  );
};
